import { DEFAULT_BRAND_COLOR } from '../config'
import { WebsiteDocument } from '../schema/document'
import {
  SiteSettings,
  siteSectionsSchema,
  siteSettingsSchema
} from '../schema/site'
import { TEMPLATES, TemplateDefinition } from './registry'

/**
 * The theme a template page is built with.
 *
 * The form's answers and the template's own values for everything else. The
 * hand-picked families and the colours set role by role are the form's answers
 * now, not the old builder's: the settings form has both under its advanced
 * box, and that box opens itself for a theme that carries either, so a page
 * that does not use its pair and its brand colour cannot look as if it does.
 * That is what makes them safe to carry, and it is the whole of what is
 * carried: everything else comes from the template's base.
 */
export function templateTheme(
  fromForm: Pick<
    WebsiteDocument['theme'],
    'brandColor' | 'fontPairId' | 'fonts' | 'colors'
  >,
  base: TemplateDefinition['base'] = TEMPLATES.classic.base
): WebsiteDocument['theme'] {
  return {
    ...base,
    // Always a real colour, so the page is always derived and never falls back
    // to a preset the template does not offer.
    brandColor: fromForm.brandColor ?? DEFAULT_BRAND_COLOR,
    fontPairId: fromForm.fontPairId,
    // Absent rather than empty when nothing was chosen by hand: the pair and
    // the derivation are then the whole answer, and the document says so.
    ...(fromForm.fonts ? { fonts: fromForm.fonts } : {}),
    ...(fromForm.colors ? { colors: fromForm.colors } : {})
  }
}

/** What the settings form opens on for a venue that has not filled it in yet. */
export function defaultSiteSettings(defaultLanguage: string): SiteSettings {
  return {
    name: '',
    tagline: { [defaultLanguage]: '' },
    description: { [defaultLanguage]: '' },
    street: '',
    postalCode: '',
    city: '',
    phone: '',
    email: '',
    // Seven days, open by default. An owner corrects the two that differ rather
    // than filling in all seven.
    hours: [0, 1, 2, 3, 4, 5, 6].map(weekday => ({
      weekday,
      closed: false,
      opensAt: '12:00',
      closesAt: '22:00'
    })),
    templateId: 'classic',
    sections: siteSectionsSchema.parse({})
  }
}

const sectionsHoldNoPhoto = (sections: unknown) => {
  if (!sections || typeof sections !== 'object') return true
  const value = sections as {
    hero?: { photo?: unknown }
    about?: { photo?: unknown }
    gallery?: { photos?: unknown }
  }
  return (
    !value.hero?.photo &&
    !value.about?.photo &&
    !(Array.isArray(value.gallery?.photos) && value.gallery.photos.length > 0)
  )
}

/**
 * Settings in the shape the form and the templates read, whatever shape they
 * were stored in.
 *
 * Documents written before the sections existed kept their photos in one list
 * by position: the first behind the name, the second beside the description,
 * the rest the gallery. Those are lifted into the sections here, once, and the
 * list is dropped. Everything else is the schema's defaults filling in what an
 * older document never had to say.
 *
 * Never throws into the form: settings the schema refuses become a blank form
 * rather than a blank screen.
 */
export function normalizeSiteSettings(
  raw: unknown,
  defaultLanguage: string
): SiteSettings {
  if (!raw || typeof raw !== 'object') return defaultSiteSettings(defaultLanguage)

  const input = raw as Record<string, unknown>
  const lifted: Record<string, unknown> = { ...input }
  delete lifted.photos

  if (Array.isArray(input.photos) && sectionsHoldNoPhoto(input.sections)) {
    const photos = input.photos.map(photo =>
      typeof photo === 'string' ? photo : ''
    )
    const sections = (input.sections ?? {}) as Record<string, unknown>
    lifted.sections = {
      ...sections,
      hero: { ...(sections.hero as object), ...(photos[0] ? { photo: photos[0] } : {}) },
      about: { ...(sections.about as object), ...(photos[1] ? { photo: photos[1] } : {}) },
      gallery: {
        ...(sections.gallery as object),
        photos: photos.slice(2).filter(Boolean)
      }
    }
  }

  const parsed = siteSettingsSchema.safeParse(lifted)
  return parsed.success ? parsed.data : defaultSiteSettings(defaultLanguage)
}

/**
 * Whether the settings form has been filled in.
 *
 * Every text field, not only the name. The template builds a whole page out of
 * these answers and leaves out any section it has nothing for, so a form saved
 * half empty produces a page missing its address, its contact details or its
 * description, with nothing on screen to say why.
 *
 * A translated field counts when it is written in the language being edited
 * or in the page's default one, which is what the page shows in its place
 * (`render/context.ts`). The facts are typed in Settings, in one language, so
 * asking for each page language in turn refused every page but that one. The
 * description is only asked for while the section that shows it is on: a
 * field nobody will see must not block the page.
 */
export function siteSettingsComplete(
  settings: SiteSettings,
  language: string,
  defaultLanguage: string = language
): boolean {
  const filled = (value: string | undefined) => Boolean(value && value.trim())
  const translated = (field: Record<string, string>) =>
    filled(field[language]) || filled(field[defaultLanguage])
  return (
    filled(settings.name) &&
    translated(settings.tagline) &&
    (!settings.sections.about.enabled ||
      translated(settings.description)) &&
    filled(settings.street) &&
    filled(settings.postalCode) &&
    filled(settings.city) &&
    filled(settings.phone) &&
    filled(settings.email)
  )
}
